import QuestionCircleIcon from '@atlaskit/icon/glyph/question-circle';
import { colors } from '@atlaskit/theme';
import React, { useEffect, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import config from 'appconfig';
import Tooltip from 'components/common/Tooltip';
import { CloudProductKeys } from 'data/products/keys';
import { State } from 'model/State';
import t from 'modules/i18n/intl';
import { hostedAccountTypes } from 'util/mac/helpers';

import {
  selectDetails,
  selectIsSuspended,
  selectOrderableItemByAccountId,
  selectRenewalTotal,
} from '../duck/selectors';
import { DetailsRow } from './licensesExpandedRow/DetailsRowContentElements/DetailsRow';
import { fetchOrderableItemByAccountId } from '../duck/actions/product';
import { removeTimezone } from '../common/utils';

const ChildDescription = ({ childId }) => {
  const childOrderableItem = useSelector((state: State) =>
    selectOrderableItemByAccountId(state, childId),
  );
  const [childDescription, setChildDescription] = useState('');

  useEffect(() => {
    if (childOrderableItem) {
      setChildDescription(childOrderableItem.merchandise.description);
    }
  }, [childOrderableItem]);

  return (
    childDescription && (
      <>
        <br />
        {childDescription}
      </>
    )
  );
};

const contactUs = (chunks) => <a href={config.companyContact}>{chunks}</a>;

const PaymentDetailsInfoSection = () => {
  const dispatch = useDispatch();

  const accountId = useSelector((state: State) => state.location.payload.accountId);
  const accountDetails = useSelector((state: State) => selectDetails(state, accountId));
  const isAccountSuspended = useSelector(selectIsSuspended);
  const orderableItem = useSelector((state: State) =>
    selectOrderableItemByAccountId(state, accountId),
  );
  const renewalTotal = useSelector((state: State) => selectRenewalTotal(state, accountId));

  const [accountInfoValue, setAccountInfoValue] = useState('');
  const [descriptionValue, setDescriptionValue] = useState('');
  const [endDate, setEndDate] = useState<string>('');

  useEffect(() => {
    if (accountDetails) {
      setAccountInfoValue(
        accountDetails.merchandise?.productPackage.key === CloudProductKeys.BITBUCKET ||
          !hostedAccountTypes.includes(accountDetails.merchandise?.productPackage.productType)
          ? accountDetails.name
          : accountDetails.accountUrl,
      );
      setEndDate(accountDetails.latestPeriod?.endDate ?? '');
    }
  }, [accountDetails]);

  useEffect(() => {
    if (orderableItem?.details.amount > 0) {
      setDescriptionValue(orderableItem?.merchandise.description);
      if (accountDetails) {
        accountDetails.children.forEach(({ active, renewalAction, id }) => {
          if (active && renewalAction !== 'NONE') {
            dispatch(fetchOrderableItemByAccountId(id));
          }
        });
      }
    }
  }, [orderableItem, accountDetails]);

  const endDateWithoutTZ = removeTimezone(endDate);

  return (
    <>
      <DetailsRow
        label={
          <FormattedMessage
            defaultMessage="Account information"
            id="mac.payment-details-page.account-information"
          />
        }
        value={accountInfoValue}
        width={60}
        boldValue
      />
      <DetailsRow
        label={
          <FormattedMessage
            defaultMessage="Description"
            id="mac.payment-details-page.description"
          />
        }
        value={
          <>
            {descriptionValue}
            {accountDetails?.children?.map(({ id }) => (
              <ChildDescription childId={id} key={id} />
            ))}
          </>
        }
        width={60}
      />
      {isAccountSuspended ? (
        t(
          {
            id: 'mac.update.card.renewal.total.error.message',
            defaultMessage:
              'We are currently unable to calculate your renewal total. Please, <contactUs>contact us</contactUs>.',
          },
          {
            contactUs,
          },
        )
      ) : (
        <DetailsRow
          label={
            <>
              <FormattedMessage
                defaultMessage="Estimate price"
                id="mac.payment-details-page.estimate-price"
              />
              <Tooltip
                direction="up"
                content={
                  <FormattedMessage
                    id="mac.payment-details.estimate-price.tooltip"
                    defaultMessage="This estimate is based on your current users and apps, including tax."
                  />
                }
              >
                <QuestionCircleIcon
                  size="small"
                  primaryColor={colors.N80}
                  label="estimate-price-tooltip"
                />
              </Tooltip>
            </>
          }
          value={renewalTotal}
          width={60}
        />
      )}
      {endDate && (
        <DetailsRow
          label={
            new Date(endDateWithoutTZ) > new Date() ? (
              <FormattedMessage
                defaultMessage="Renews on"
                id="mac.payment-details-page.renews-on"
              />
            ) : (
              <FormattedMessage
                defaultMessage="Expired on"
                id="mac.payment-details-page.expired-on"
              />
            )
          }
          value={
            <FormattedDate value={endDateWithoutTZ} year="numeric" month="long" day="2-digit" />
          }
          width={60}
        />
      )}
    </>
  );
};

export default PaymentDetailsInfoSection;
